import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaTimes, FaExternalLinkAlt, FaCalendar, FaCode } from "react-icons/fa";
import { isImageUrl } from "../utils/media";

const ProjectModal = ({ project, isOpen, onClose }) => {
  const dialogRef = useRef(null);
  const previousFocusRef = useRef(null);

  // Escape-to-close, focus management, tab trap, and body scroll lock
  useEffect(() => {
    if (!isOpen) return;

    previousFocusRef.current = document.activeElement;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab" || !dialogRef.current) return;

      const focusable = dialogRef.current.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
      previousFocusRef.current?.focus?.();
    };
  }, [isOpen, onClose]);

  if (!project) return null;

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.3 },
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.3 },
    },
  };

  const modalVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      y: 50,
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: 50,
      transition: { duration: 0.3 },
    },
  };

  const techStack = Array.isArray(project.techStack) ? project.techStack : [];
  const createdDate = project.createdAt
    ? new Date(project.createdAt).toLocaleDateString()
    : "Recently";

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose}
          >
            {/* Modal */}
            <motion.div
              ref={dialogRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="project-modal-title"
              tabIndex={-1}
              className="bg-white dark:bg-brand-darker rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-gray-200 dark:border-brand-purple/20 focus:outline-none"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="relative">
                {/* Project Preview */}
                <div className="h-64 md:h-80 bg-gradient-to-br from-brand-gold/20 to-brand-purple/20 relative overflow-hidden">
                  {project.thumbnail &&
                    (isImageUrl(project.thumbnail) ? (
                      <img
                        src={project.thumbnail}
                        alt={`Screenshot of ${project.title}`}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    ) : (
                      <iframe
                        src={project.thumbnail}
                        className="absolute inset-0 w-full h-full border-0"
                        title={project.title}
                        loading="lazy"
                        sandbox="allow-scripts allow-same-origin"
                      />
                    ))}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />

                  {/* Close button */}
                  <button
                    onClick={onClose}
                    aria-label="Close project details"
                    className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors z-10"
                  >
                    <FaTimes className="w-5 h-5" />
                  </button>

                  {/* Project title overlay */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <h2
                      id="project-modal-title"
                      className="text-2xl md:text-3xl font-bold text-white mb-2"
                    >
                      {project.title}
                    </h2>
                    <div className="flex items-center gap-4 text-gray-300">
                      <span className="flex items-center gap-1">
                        <FaCalendar className="w-4 h-4" />
                        {createdDate}
                      </span>
                      {techStack.length > 0 && (
                        <span className="flex items-center gap-1">
                          <FaCode className="w-4 h-4" />
                          {techStack.length} technologies
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 md:p-8 overflow-y-auto max-h-[60vh]">
                {/* Description */}
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-brand-gold mb-3">
                    About This Project
                  </h3>
                  <p className="text-gray-700 dark:text-brand-cream leading-relaxed text-lg">
                    {project.description}
                  </p>
                </div>

                {/* Case study sections */}
                {(project.challenge || project.approach || project.outcome) && (
                  <div className="mb-6 space-y-5">
                    {project.challenge && (
                      <div>
                        <h3 className="text-xl font-semibold text-brand-gold mb-2">
                          Challenge
                        </h3>
                        <p className="text-gray-700 dark:text-brand-cream leading-relaxed">
                          {project.challenge}
                        </p>
                      </div>
                    )}
                    {project.approach && (
                      <div>
                        <h3 className="text-xl font-semibold text-brand-gold mb-2">
                          Approach
                        </h3>
                        <p className="text-gray-700 dark:text-brand-cream leading-relaxed">
                          {project.approach}
                        </p>
                      </div>
                    )}
                    {project.outcome && (
                      <div>
                        <h3 className="text-xl font-semibold text-brand-gold mb-2">
                          Outcome
                        </h3>
                        <p className="text-gray-700 dark:text-brand-cream leading-relaxed">
                          {project.outcome}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Tech Stack (only when real data exists) */}
                {techStack.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-brand-gold mb-3">
                      Technology Stack
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {techStack.map((tech, index) => (
                        <motion.span
                          key={tech}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: index * 0.1 }}
                          className="px-3 py-1 bg-purple-100 dark:bg-brand-purple/20 text-gray-700 dark:text-brand-cream rounded-full text-sm font-medium border border-purple-200 dark:border-brand-purple/30 hover:bg-purple-200 dark:hover:bg-brand-purple/30 transition-colors"
                        >
                          {tech}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                {project.link && (
                  <div className="flex flex-col sm:flex-row gap-4">
                    <motion.a
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 px-6 py-3 bg-brand-gold text-black font-semibold rounded-lg hover:bg-brand-gold/90 hover:shadow-glow transition-all duration-300"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <FaExternalLinkAlt className="w-4 h-4" />
                      View Live Project
                    </motion.a>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ProjectModal;
